import React from "react";
import {autorun, runInAction} from "mobx";

import "./popup.scss";

import EsriPopup from "@arcgis/core/widgets/Popup";
import EsriPopupViewModel from "@arcgis/core/widgets/Popup/PopupViewModel";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriImageryLayer from "@arcgis/core/layers/ImageryLayer";
import EsriMapImageLayer from "@arcgis/core/layers/MapImageLayer";
import EsriGraphic from "@arcgis/core/Graphic";
import EsriPopupTemplate from "@arcgis/core/PopupTemplate";
import EsriHandles from "@arcgis/core/core/Handles";
import esriRequest from "@arcgis/core/request";
import {PopupPanel} from "./popup-panel/popup-panel";
import {ContentConfiguration, CustomChartConfiguration} from "../../config/content-configuration";

import {PopupConfiguration, PopupLayerGroupConfiguration} from "../../config/popup-configuration";
import {observer} from "mobx-react";

import {
    ExternalServiceConfiguration,
    ExternalServiceDataMappingConfiguration
} from "../../config/external-service-configuration";
import {Accordion, AccordionTitleProps, Button, Image, Popup as SemanticPopup, Transition} from "semantic-ui-react";
import {PathUtils} from "../../../utils/path-utils";
import {ActionButtonConfiguration} from "../../config/action-configuration";
import {Icon} from "../icon/icon";
import EsriActionButton from "@arcgis/core/support/actions/ActionButton";
import {SimpleMarkerSymbolConfiguration} from "../../config/symbol-configuration";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import {MapUtils} from "../../../utils/map-utils";
import {PopupPanelItemChart} from "./popup-panel/popup-panel-item/popup-panel-item-chart";
import {IAppStore, withStore} from "../../stores/abstract-app-store";
import {PopupPanelInfo, PopupStore} from "../../stores/popup-store-mobx";
import FetchPopupFeaturesResult = __esri.FetchPopupFeaturesResult;
import FillSymbolProperties = __esri.FillSymbolProperties;
import {CopyToClipboard} from "react-copy-to-clipboard";


interface PopupProps {
    config: PopupConfiguration;
    onLoad?: (sopupViewModel: EsriPopupViewModel) => any;
    store?: IAppStore; // this is injected by withStore
}

// interface ScreenPoint { x:number, y:number };
@observer
export class PopupBase extends React.Component<PopupProps, any> {
    static readonly highlightLayerId = "popup highlight layer";

    config: PopupConfiguration;
    watchHandles: any[] = [];
    mapClickWatchHandle: any;
    watchHandlesPosition: any[] = [];
    autorunDisposers: any[] = [];
    firstFlag = true;
    popup: any;
    popupRef: any;
    pointerRef: any;
    preparedLayers: string[] = [];
    handles: EsriHandles;
    highlightLayer: EsriGraphicsLayer;
    avlsLayer: EsriFeatureLayer | EsriMapImageLayer | null = null;
    actionMap: HashMap<number> = {};
    mapPoint?: __esri.Point;
    featureCount = 0;

    objectMap: Map<string, number> = new Map();
    rasterPrepared: string[] = [];
    rasterMetauidPrepared: string[] = [];
    rasterServiceMap: Map<string, string> = new Map();

    constructor(props: PopupProps) {
        super(props);
        this.state = {
            viewModel: null,
            coordinate: null,
            copied: {e: false, n: false, both: false},
            isLoading: undefined,
            elevation: new Map(),
            pointerPosition: {top: 0, left: 0}
        };
        this.popupRef = React.createRef();
        this.pointerRef = React.createRef();
        this.handleClick = this.handleClick.bind(this);
        this.handleHighlightClick = this.handleHighlightClick.bind(this);
        this.highlight = this.highlight.bind(this);
        this.handleZoomInClick = this.handleZoomInClick.bind(this);
        this.close = this.close.bind(this);
        this.prepareLayers = this.prepareLayers.bind(this);
        this.customChart = this.customChart.bind(this);
        this.externalServiceLoader = this.externalServiceLoader.bind(this);
        this.handles = new EsriHandles();
        this.highlightLayer = new EsriGraphicsLayer({listMode: "hide", title: PopupBase.highlightLayerId});
    }

    componentDidMount(): void {
        const { onLoad, store } = this.props;

        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (store?.uiStore.widgets.has("popup")) {
                reaction.dispose(); // stop autorun
                return;
            }

            if (!store?.mapStore.view || !store.PopupStore) {
                return;
            }
            this.config = store.mapViewerConfig.popup;
            const popupStore = store.PopupStore;

            const popup = this.loadPopup(store.mapStore.view);
            if (!popup) {
                return;
            }
            this.popup = popup;

            this.prepareLayers(store.mapStore.view.map, popupStore, store);
            this.watchHandles.push(
                store.mapStore.view.map.watch("allLayers.length",
                    (newValue, oldValue, propertyName, target: __esri.WebMap) => {
                        this.prepareLayers(target, popupStore, store);
                    })
            );
            store.mapStore.view.popupEnabled = false;
            this.setState({
                coordinate: null
            });

            store.mapStore.view.map.add(this.highlightLayer);
            // const addLayers: EsriFeatureLayer[] = [];
            // let avls: EsriFeatureLayer | null = null;
            if (typeof this.config.layers !== "undefined" && this.config.layers !== null){
                if (this.config.layers.avls) {
                    const avlsConfig = this.config.layers.avls;
                    const avlsTemplate = store.mapViewerConfig.getLayerTemplate("avls");
                    if (avlsTemplate?.template && typeof avlsTemplate?.template !== "string") {
                        avlsConfig.popupTemplate = avlsTemplate.template;
                    }
                    if(avlsConfig.layerType === "feature") {
                        this.avlsLayer =
                            new EsriFeatureLayer(avlsConfig as __esri.FeatureLayerProperties);
                    } else if(avlsConfig.layerType === "map-image") {
                        this.avlsLayer =
                            new EsriMapImageLayer(avlsConfig as __esri.MapImageLayerProperties);
                    }
                    if (this.avlsLayer) {
                        this.avlsLayer.load().then((value: any) => {
                            if (value.layerType === "feature") {
                                this.prepareExternalLoader(store, value);
                                this.prepareActions(store, value);
                            } else if (value.layerType === "map-image") {
                                value.allSublayers.forEach((layer: __esri.Sublayer) => {
                                    if (avlsConfig.popupTemplate) {
                                        layer.popupTemplate = new EsriPopupTemplate(
                                            avlsConfig.popupTemplate as
                                                __esri.PopupTemplateProperties);
                                        layer.definitionExpression = avlsConfig.definitionExpression;
                                        this.prepareExternalLoader(store, layer);
                                        this.prepareActions(store, layer);
                                    }
                                });
                            }
                        });
                    }
                }
            }
            if (!store.PopupStore.disabled) {
                // add watch for clicks on the map view
                this.addMapClickWatch(store.mapStore.view, store);
            }
            // react when popup is enabled / disabled
            this.autorunDisposers.push(
                autorun(() => {
                    if (store.PopupStore.disabled) {
                        this.removeMapClickWatch();
                        this.clearGraphicLayer(store?.mapStore.view);
                        this.clearHighlights();
                    } else {
                        if (!store?.mapStore.view) {
                            return;
                        }
                        this.addMapClickWatch(store.mapStore.view, store);
                    }
                })
            );

            const highlightSymbol = this.config.highlightSymbol as FillSymbolProperties;
            // This event fires for each click on any popup action
            // popup.on("trigger-action", (event) => {});
            this.watchHandles.push(
                reactiveUtils.watch(
                    () => this.popup.visible,
                    (newValue: boolean) => {
                        store.PopupStore.setVisible(newValue);
                    })
            );
            this.watchHandles.push(
                reactiveUtils.watch(
                    () => this.popup?.selectedFeature,
                    (graphic: any) => {
                        if (this.popup.highlightEnabled) {
                            this.clearHighlights();
                            if (graphic) {
                                if (graphic.sourceLayer.opacity === 0) {
                                    this.highlightLayer.add(
                                        new EsriGraphic({geometry: graphic.geometry, symbol: highlightSymbol}));
                                }
                            }
                        }
                    })
            );
            this.setState({
                viewModel: this.popup.viewModel
            });
            store.uiStore.addWidget("popup", this);

            if (onLoad) {
                // call the hook
                onLoad(this.popup.viewModel);
            }
            // react when popup is hidden
            this.autorunDisposers.push(autorun(() => {
                if(!store.PopupStore.visible) {
                    this.close(store);
                }
            }));
            // react when mapPoint or features change
            this.autorunDisposers.push(autorun(() => {
                if (store.PopupStore.mapPoint){
                    if (this.mapPoint?.x !== store.PopupStore.mapPoint.x
                        || this.mapPoint?.y !== store.PopupStore.mapPoint.y) {
                        this.open(store);
                    } else if (store.PopupStore.features.length > 0
                        && store.PopupStore.features.length !==  this.featureCount) {
                        this.addFeatures(store, store.PopupStore.features);
                    }
                }else{
                    this.mapPoint = undefined;
                }
            }));
            reaction.dispose();
        }));
    }
    componentWillUnmount(): void {
        const {store} = this.props;

        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.removeMapClickWatch();
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.removePositionHandles();
        store?.PopupStore.clearTimeouts();
        store?.mapStore.view?.map.remove(this.highlightLayer);
    }

    render(): React.ReactElement | null {
        const { coordinate, elevation, isLoading, pointerPosition } = this.state;
        const { config, store} = this.props;
        if (!store) {
            return null;
        }
        const popupStore = store.PopupStore;
        if (!popupStore) {
            return null;
        }

        let coords: React.ReactElement | null = null;
        let pointerSymbol: React.ReactElement | null = null;
        if (coordinate && config.visibleElements.coordinates){
            if (config.pointerSymbol && config.pointerSymbol.type === "picture-marker") {
                pointerSymbol = (
                    <Image src={config.pointerSymbol.url} height="28" verticalAlign="middle" floated="left" />
                );
            }
            const e = ""+Math.round(coordinate.x);
            const n = ""+Math.round(coordinate.y)
            const eFormated = new Intl.NumberFormat("de-CH").format(e);
            const nFormated = new Intl.NumberFormat("de-CH").format(n);
            const eElement = [...elevation.values()].filter(
                (el) => el.value > 0)
                .map((el) =>
                    new Intl.NumberFormat("de-CH").format(Math.round(el.value)) + " m.ü.M. (" + el.title + ")");
            let eString = "";
            if (eElement.length > 0) {
                eString = "Höhe: " + eElement.join(", ");
            }
            coords = (<div>Lage (E/N): <SemanticPopup
                content={this.state.copied.e ?
                    <>E Koordinate kopiert <Icon name={"check"} /></>: "E Koordinate kopieren" }
                trigger={
                    <span>
                        <CopyToClipboard
                            text={e}
                            onCopy={() => this.setState({copied: {e: true, n: false, both: false}})}>
                            <span className={"geo-copy_to_clipboard"+(this.state.copied.e ? "geo-copied": "")}>
                                {eFormated}</span>
                        </CopyToClipboard>
                    </span>}
                on={"hover"}
            /> / <SemanticPopup
                content={this.state.copied.n ?
                    <>N Koordinaten kopiert <Icon name={"check"} /></>: "N Koordinate kopieren" }
                trigger={
                    <span>
                        <CopyToClipboard
                            text={n}
                            onCopy={() => this.setState({copied: {e: false, n: true, both: false}})}>
                            <span className={"geo-copy_to_clipboard"+(this.state.copied.n ? " geo-copied": "")}>
                                {nFormated}</span>
                        </CopyToClipboard>
                    </span>}
                on={"hover"}
            /> <SemanticPopup
                content={this.state.copied.both ?
                    <>Koordinaten kopiert <Icon name={"check"} /></>: "Koordinaten kopieren" }
                offset={[-10, 0]}
                trigger={
                    <span>
                        <CopyToClipboard
                            text={e+" / "+ n}
                            onCopy={() => this.setState({copied: {e: false, n: false, both: true}})}>
                            <Button
                                className={this.state.copied.both ? "success": ""}
                                compact
                                size={"mini"}
                                positive={this.state.copied.both}>
                                <Icon name={"clipboard"} />
                            </Button>
                        </CopyToClipboard>
                    </span>}
                on={"hover"}
            />
            <br />{eString}</div>);
        }

        let addClass = "geo-popup geo-component geo-widget";
        // Wird in ÖV Netz für Streckenauswahl verwendet
        if(typeof store?.PopupStore.selected === "string"
            && this.objectMap.has(store?.PopupStore.selected as string)){
            addClass += " nth-object-"+this.objectMap.get(store?.PopupStore.selected as string);
        }

        let popupContent = null;
        if (config.visibleElements.featureNavigation) {
            popupContent = <div ref={this.popupRef}/>;
        } else if (popupStore.panels.length > 0) {
            const panels: any[] = popupStore.panels.map((panelProps: PopupPanelInfo) =>
                <PopupPanel {...panelProps}
                    key={panelProps.index+panelProps.title}
                    config={this.props.config}
                    onClick={this.handleClick}
                    onHighlightClick={this.handleHighlightClick}
                    onHighlight={this.highlight}
                    onZoomInClick={this.handleZoomInClick} />
            );
            popupContent = (
                <Accordion className={"geo-popup-accordion"}
                    styled fluid={true}
                    defaultActiveIndex={popupStore.activeIndex}
                    exclusive={false}
                    panels={panels}/>
            );
        }

        if (config.dockEnabled) {
            addClass += " docked docked-" + config.dockOptions.position;
            return (
                <>
                    <div
                        className={"geo-popup-spinner"
                            + (isLoading!==undefined?(isLoading? " geo-spinner-visible": " geo-spinner-hidden"):"")}
                        style={pointerPosition}>
                        <Icon name={"recycle"} className={"geo-rotating"} selected={true}/>
                    </div>
                    <Transition visible={store?.PopupStore.visible} duration={0} mountOnShow={false}>
                        <div className={addClass}>
                            <div className="geo-popup-content">
                                <div className={"geo-close"} onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    this.close(store);
                                }} ><Icon name="navigate_cross" /></div>
                                <div className="geo-popup-features">
                                    {popupContent}
                                </div>
                                <div className="popup-content-coordinates">
                                    {pointerSymbol}{coords}
                                </div>
                            </div>
                        </div>
                    </Transition>
                </>
            );
        } else {
            addClass += " not-docked";
            return (
                <div>
                    <div
                        className={"geo-popup-spinner"
                            + (isLoading!==undefined?(isLoading? " geo-spinner-visible": " geo-spinner-hidden"):"")}
                        style={pointerPosition}>
                        <Icon name={"recycle"} className={"geo-rotating"} selected={true}/>
                    </div>
                    <div ref={this.pointerRef} className="geo-popup-pointer-ref" style={pointerPosition} />
                    <SemanticPopup open={store?.PopupStore.visible}
                        className={"geo-popup"}
                        context={this.pointerRef} wide={"very"} popperDependencies={
                            [pointerPosition, popupStore.lastItemTitle]}>
                        <Icon className={"geo-close"} name="navigate_cross" onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            this.close(store);
                        }} />
                        <div className={addClass}>
                            <div className="geo-popup-content">
                                <div className="geo-popup-features">
                                    {config.visibleElements.featureNavigation ?
                                        <div ref={this.popupRef}/>
                                        :
                                        popupContent
                                    }
                                </div>
                                <div className="popup-content-coordinates">{pointerSymbol}{coords}</div>
                            </div>
                        </div>
                    </SemanticPopup>
                </div>
            );
        }
    }

    private addFeatures(store: IAppStore, features: __esri.Graphic[]): void {
        const popupStore = store?.PopupStore;
        if (!popupStore) {
            return;
        }

        this.firstFlag = false;
        this.setState({isLoading: true});
        features.forEach((graphic: __esri.Graphic) => {
            // sourceLayer on Graphic is not documented!
            const source = (graphic as any).sourceLayer as __esri.Layer;
            if (source) {
                const url = PathUtils.getLayerPath(source);
                let metauid = "custom";
                if (typeof url === "string") {
                    metauid = PathUtils.getMetaId(url);
                }
                this.prepareTemplate(source, metauid, popupStore, store);
                this.prepareUnits(source, popupStore);
                this.prepareExternalLoader(store, source);
                this.prepareActions(store, source);
            }
        });
        this.prepareFeatures(store, features);
        this.setState({
            isLoading: false
        });
    }

    private open(store: IAppStore): void {
        if (!store || !store.mapStore.view || !store.PopupStore.mapPoint) {
            return;
        }
        const popupStore = store.PopupStore;
        if (popupStore.disabled) {
            return;
        }
        this.objectMap=new Map();
        popupStore.setSelected();
        popupStore.setActivePages([0, 1]);
        this.setState({isLoading: true, copied: {e: false, n: false, both: false}});
        this.handles.removeAll();
        popupStore.clearPanels();
        this.mapPoint = store.PopupStore.mapPoint;
        if(!this.mapPoint){
            this.close(store);
            return;
        }
        store.PopupStore.peparedFields = [];
        const mapPoint: __esri.Point = this.mapPoint;
        const features = store.PopupStore.features;
        this.featureCount = features.length;
        const screenPoint = store.mapStore.view.toScreen(mapPoint);
        this.clearGraphicLayer(store?.mapStore.view);
        if (this.config.dockEnabled && features.length===0) {
            const isSmallDevice =
                window.matchMedia("(max-width: " + store.mapViewerConfig.breakpoints.md+"px)").matches;
            // reposition center if needed
            if (!isSmallDevice && document.documentElement.clientWidth - screenPoint.x < 420){
                // calculate new center
                const newX = document.documentElement.clientWidth / 2
                    + 520 - (document.documentElement.clientWidth - screenPoint.x);
                store.mapStore.view.goTo({center: store.mapStore.view.toMap({ x: newX,
                    y: document.documentElement.clientHeight / 2 })}).catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
            }
        }
        // unsichtbarer Punkt welcher für die Spinner- und Popup-Positionierung verwendet wird
        store.mapStore.view.graphics.add(new EsriGraphic({
            symbol: {
                type: "simple-marker",
                style: "circle",
                size: 1,
                color: [255, 255, 255, 0]
            } as SimpleMarkerSymbolConfiguration,
            geometry: mapPoint
        }));
        this.setState({pointerPosition: {top: screenPoint.y, left: screenPoint.x - 20, width: 40}});
        this.addPositionHandles(store.mapStore.view);

        if (store.appConfig.elevationLayers && store.appConfig.elevationLayers.length > 0) {
            store.mapStore.view.map.ground.layers.forEach((eLayer)=> {
                eLayer.queryElevation(mapPoint).then((result) => {
                    if (result.geometry.type === "point") {
                        this.setState((state: any)=>{
                            const {elevation} = state;
                            if ("z" in result.geometry) {
                                elevation.set(eLayer.id, { value: result.geometry.z, title: eLayer.title});
                            } else {
                                elevation.set(eLayer.id, { value: 0 });
                            }
                            return elevation;
                        });
                    }
                });
            });
        }
        if (features.length>0){
            features.forEach((graphic: __esri.Graphic) => {
                // sourceLayer on Graphic is not documented!
                const source = (graphic as any).sourceLayer as __esri.Layer;
                if (source) {
                    const url = PathUtils.getLayerPath(source);
                    let metauid = "custom";
                    if (typeof url === "string") {
                        metauid = PathUtils.getMetaId(url);
                    }
                    this.prepareTemplate(source, metauid, popupStore, store);
                    this.prepareUnits(source, popupStore);
                    this.prepareExternalLoader(store, source);
                    this.prepareActions(store, source);
                }
            });
            this.firstFlag = true;
            this.prepareFeatures(store, features, 0, mapPoint);
            this.setState({
                isLoading: false,
                coordinate: store.mapStore.view.toMap({ x: screenPoint.x, y: screenPoint.y })
            });
            if (store.PopupStore.panels.length === 0) {
                this.close(store);
            } else {
                if (this.config.dockEnabled) {
                    this.removePositionHandles(store.mapStore.view);
                    store.mapStore.view.graphics.add(new EsriGraphic({
                        symbol: this.config.pointerSymbol,
                        geometry: mapPoint
                    }));
                }
            }
            return;
        }
        // Auf aktive Rasterlayer überprüfen
        if(this.rasterServiceMap.size && mapPoint){
            const metauids: string[] = [];
            this.rasterServiceMap.forEach((targetMetauid)=> {
                const targetLayers = store.mapStore.getLayerViewsByMetauid(targetMetauid);
                if(targetLayers.length===1){
                    const targetLayer = targetLayers[0].layer as __esri.ImageryLayer;
                    const visibleAtCurrentScale = (!targetLayer.hasOwnProperty("maxScale") || (
                        (targetLayer.minScale === 0 || targetLayer.minScale >= store.mapStore.view?.scale)
                        && targetLayer.maxScale <= store.mapStore.view?.scale));
                    // Layer muss auf aktuellem Massstab sichtbar sein und darf nur einmal verwendet werden
                    if(visibleAtCurrentScale && !metauids.includes(targetMetauid)) {
                        targetLayer.identify(
                            {geometry: mapPoint, returnGeometry: true, maxItemCount: 1}).then((response) => {
                            if (response.catalogItems.features.length > 0
                                && !Number.isNaN(parseFloat(response.value))) {
                                // Feature für Popup aufbereiten
                                response.catalogItems.features.map((graphic) => {
                                    graphic.layer = targetLayer;
                                    (graphic as any).sourceLayer = targetLayer;
                                    graphic.setAttribute("Raster.ServicePixelValue", response.value);
                                    graphic.setAttribute("Raster.ItemPixelValue", response.value);
                                    if(targetLayer.serviceRasterInfo){
                                        const values = response.value.split(", ");
                                        targetLayer.serviceRasterInfo.bandInfos.forEach((band, index)=>{
                                            graphic.setAttribute("Raster.ServicePixelValue."+band.name, values[index]);
                                            graphic.setAttribute("Raster.ItemPixelValue."+band.name, values[index]);
                                        })
                                    }
                                });
                                this.prepareFeatures(store, response.catalogItems.features,
                                    (targetLayers[0] as any).index, mapPoint);
                            }
                        });
                        metauids.push(targetMetauid);
                    }
                }
            });
        }
        this.popup.fetchFeatures(screenPoint).then((response: FetchPopupFeaturesResult) => {
            this.firstFlag = true;
            if (!response.allGraphicsPromise) {
                return;
            }
            response.allGraphicsPromise.then((graphics) => {
                if(graphics.length>0) {
                    // sourceLayer on Graphic is not documented!
                    const source = (graphics[0] as any).sourceLayer as __esri.Sublayer;
                    if (!source?.popupEnabled) {
                        return;
                    }
                }
                this.prepareFeatures(store, graphics, 0, mapPoint);
                if (!this.avlsLayer) {
                    this.setState({isLoading: false});
                    if (store.PopupStore.panels.length === 0) {
                        this.close(store);
                    } else {
                        if (this.config.dockEnabled) {
                            this.removePositionHandles(store.mapStore.view);
                            if (!store.mapStore.view) {
                                return;
                            }
                            store.mapStore.view.graphics.add(new EsriGraphic({
                                symbol: this.config.pointerSymbol,
                                geometry: mapPoint
                            }));
                        }
                    }
                }
            });
            this.setState({
                coordinate: store.mapStore.view? store.mapStore.view.toMap({ x: screenPoint.x, y: screenPoint.y }): null
            });
        });
        if (this.avlsLayer) {
            let queryLayer: __esri.FeatureLayer | __esri.Sublayer;
            if (this.avlsLayer.hasOwnProperty("allSublayers")) {
                queryLayer = (this.avlsLayer as EsriMapImageLayer).allSublayers.getItemAt(0);
            } else {
                queryLayer = this.avlsLayer as EsriFeatureLayer;
            }
            queryLayer.queryFeatures({
                geometry: mapPoint,
                outFields: ["GUELTIGKEIT", "ART", "QUELLE_LS_SR", "BFS_NR", "EGRIS_EGRID", "GBPER_CODE",
                    "GBPER_NAME", "GEMEINDE", "NUMMER", "FLAECHENMASS"],
                returnGeometry: true,
                where: queryLayer.definitionExpression
            }).then((featureSet) => {
                if (!store.mapStore.view) {
                    return;
                }
                let title = "";
                if (this.avlsLayer) {
                    title = this.avlsLayer.title;
                }
                popupStore.addPanel({
                    graphics: featureSet.features,
                    title,
                    order: -99
                });
                this.setState({isLoading: false});
                if (this.config.dockEnabled) {
                    this.removePositionHandles(store.mapStore.view);
                    store.mapStore.view.graphics.add(new EsriGraphic({
                        symbol: this.config.pointerSymbol,
                        geometry: mapPoint
                    }));
                }
                // Falls nur AVLs Daten vorhanden, das Panel auf aktiv setzen
                if (popupStore.panelIndex === 2){
                    store.PopupStore.setVisible(true);
                    popupStore.setSelectedPanelIndex(1);
                    this.highlight(1, 1, store);
                }
            });
        }
    }
    private close(store: IAppStore | undefined): void {
        if (store?.mapStore.view) {
            this.clearGraphicLayer(store?.mapStore.view);
            this.clearHighlights();
            store?.mapStore.selectFeature(null);
        }
        if (this.handles) {
            this.handles.removeAll();
        }
        store?.mapStore.view?.closePopup();
        this.removePositionHandles();
        store?.PopupStore.setVisible(false);
        store?.PopupStore.clearTimeouts();
    }

    private loadPopup(view: __esri.MapView | __esri.SceneView): EsriPopup {
        const config = this.config as __esri.PopupProperties;
        config.container = this.popupRef.current;
        const props = {
            view,
            ...config
        };
        return new EsriPopup(props);
        // return popup;
    }
    private customChart(graphic: EsriGraphic, contentConfig: CustomChartConfiguration): HTMLElement {
        const {store} = this.props;
        const view = store?.mapStore.view;
        const chart = new PopupPanelItemChart({config: contentConfig, view, graphic});
        return  chart.render();
    }
    private externalServiceLoader(graphic: EsriGraphic, serviceID: string,
                                  sourceLayer: EsriFeatureLayer | EsriImageryLayer): string | Promise<string> {
        const {store} = this.props;
        if(!store) {
            return "";
        }
        const service = store.appConfig.externalServices.find(
            (element: ExternalServiceConfiguration) => element.id === serviceID);
        if (!service) {
            return "<strong>Service nicht gefunden!</strong>";
        }
        const requestOptions = {} as __esri.RequestOptions;
        requestOptions.responseType = service.xhrOptions.responseType;
        if (service.xhrOptions.usePost) {
            requestOptions.method = "post";
        }
        if (service.xhrOptions.timeout) {
            requestOptions.timeout = service.xhrOptions.timeout;
        }
        const requestData: any = {
            version: PathUtils.getVersion(PathUtils.getLayerPath(sourceLayer))
        };
        service.xhrOptions.dataMapping.forEach((mappingObj: ExternalServiceDataMappingConfiguration) => {
            if (typeof mappingObj.source === "string") {
                if (mappingObj.source === "layer") {
                    if (typeof mappingObj.map !== "undefined") {
                        let layerId: number;
                        if (sourceLayer instanceof EsriFeatureLayer) {
                            layerId = sourceLayer.layerId;
                        } else {
                            // sourceLayer on Graphic is not documented!
                            layerId = (graphic as any).sourceLayer.id;
                        }
                        if (mappingObj.map[layerId]) {
                            requestData[mappingObj.requestParam] = mappingObj.map[layerId];
                        }
                    }
                    //
                } else if (typeof mappingObj.property === "string") {
                    const tempAttribute = graphic.getAttribute(mappingObj.property);
                    if (typeof tempAttribute !== "undefined") {
                        requestData[mappingObj.requestParam] = tempAttribute;
                    }
                }
            } else if (typeof mappingObj.property === "string") {
                const tempAttribute = graphic.getAttribute(mappingObj.property);
                if (typeof tempAttribute !== "undefined") {
                    requestData[mappingObj.requestParam] = tempAttribute;
                }
            }
        });
        requestOptions.body = JSON.stringify({ params: [requestData]});
        return this.loadRequest(service, requestOptions, graphic, sourceLayer);
    }
    private loadRequest(service, requestOptions, graphic, sourceLayer): string | Promise<string> {
        return esriRequest(service.url, requestOptions).then((response: __esri.RequestResponse) => {
            const {store} = this.props;
            let htmlData = "";
            if ( response.data !== "undefined"){
                htmlData = response.data.replaceAll("href=\"#", "target=\"_self\" href=\""+window.location.href+"#");
                htmlData.match(/data-object="(.+?)"/g)?.forEach((o, index)=>{
                    this.objectMap.set(o.substring(13, o.length-1), index+1);
                });
            }
            if(service.updateIntervall){
                const firstE = htmlData.match(/<(.+?)>/);
                if(firstE){
                    const className = "geo-service-"+PathUtils.murmurhash3(sourceLayer.uid + graphic.getObjectId());
                    let newElement = firstE[0];
                    if(firstE[0].indexOf("class")>0){
                        newElement = firstE[0].replace(/class="(.+?)"/, "class=\"$1 "+className+"\"");
                    }else{
                        newElement="<"+firstE[1]+" class=\""+className+"\"";
                    }
                    htmlData = htmlData.replace(/<(.+?)>/, newElement);
                    const currentElement = document.getElementsByClassName(className);
                    if(currentElement.length===1){
                        currentElement[0].parentElement.innerHTML = htmlData;
                    }
                    // Timeout ID im Store ablegen, damit auch das PopupPanelItem diese wieder löschen kann.
                    store?.PopupStore.setTimeoutID(sourceLayer.uid + graphic.getObjectId(), setTimeout(()=>{
                        this.loadRequest(service, requestOptions, graphic, sourceLayer);
                    },service.updateIntervall*1000));
                }
            }
            return htmlData;
        }).catch(() => "<div class=\"error\">" + service.errorMessage + "</div>");
    }
    private handleClick(e: React.MouseEvent, titleProps: AccordionTitleProps): void {
        const {store} = this.props;
        if (!store) {
            return;
        }
        const popupStore = store.PopupStore;
        const { index } = titleProps;

        let newIndexes;
        let newSelectedPanelIndex = popupStore.selectedPanelIndex;
        if (popupStore.activeIndex.includes(+`${index}`)) {
            // hide tab by removing index from list
            newIndexes = popupStore.activeIndex.filter((item: number) => item !== index);
            if (popupStore.selectedPanelIndex === index ) {
                newSelectedPanelIndex = -1;
            }
            this.highlight((index as number) * -1, 0, store);
        } else {
            // show panel by adding to list
            if (this.config.exclusiveFeature) {
                // only open the exclusive tab
                newIndexes = [+`${index}`];
            } else {
                // store accordion index unique and sorted
                newIndexes = [...new Set([...popupStore.activeIndex, +`${index}`])].sort();
            }
            this.highlight(index as number, popupStore.getActivePage(index as number), store);
            newSelectedPanelIndex = index as number;
        }
        // TODO: ev. zusammen mit einer action ausführen
        popupStore.setSelectedPanelIndex(newSelectedPanelIndex);
        popupStore.setActiveIndex(newIndexes);
    }
    private handleHighlightClick(e: React.MouseEvent, panelIndex: number, index: number): void {
        const {store} = this.props;
        if (!store) {
            return;
        }
        const popupStore = store.PopupStore;
        if (panelIndex !== popupStore.selectedPanelIndex){
            this.highlight(panelIndex, index, store);
            popupStore.setSelectedPanelIndex(panelIndex);
        }
    }
    private handleZoomInClick(e: React.MouseEvent, panelIndex: number, index: number): void {
        const {store} = this.props;
        const featureGraphic = store?.PopupStore.panels[panelIndex - 1];
        const graphicIndex = index - 1;
        if (featureGraphic && typeof store?.mapStore.view !== "undefined") {
            this.goToGeometries(featureGraphic.graphics[graphicIndex].geometry);
        }
    }
    private goToGeometries(geometries: __esri.Geometry | __esri.Geometry[]): void {
        const {store} = this.props;
        if (!store?.mapStore.view) {
            return;
        }
        store.mapStore.view.goTo(MapUtils.calculateMinExtent(geometries)).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    private highlight(panelIndex: number, index: number, store: IAppStore): void {
        if (!store) {
            return;
        }
        const popupStore = store.PopupStore;

        const graphicIndex = index - 1;
        if (panelIndex < 0){
            if (popupStore.selectedPanelIndex < 0
                || Math.abs(panelIndex) === popupStore.selectedPanelIndex
                || !popupStore.activeIndex.includes(popupStore.selectedPanelIndex)) {
                this.handles.removeAll();
                if (typeof store.mapStore.view !== "undefined") {
                    this.clearHighlights();
                }
            }
        } else {
            const featureGraphic = popupStore.panels[panelIndex -1 ];
            const highlightSymbol = this.config.highlightSymbol as FillSymbolProperties;
            if (featureGraphic && this.popup.highlightEnabled && typeof store.mapStore.view !== "undefined") {
                this.handles.removeAll();
                this.clearHighlights();
                if (featureGraphic.graphics[graphicIndex]) {
                    // sourceLayer on Graphic is not documented!
                    const source = (featureGraphic.graphics[graphicIndex] as any).sourceLayer;
                    if (source.opacity === 0
                        || typeof source.layerView === "undefined"
                        || typeof source.layerView.highlight !== "function") {
                        this.highlightLayer.add(
                            new EsriGraphic({geometry: featureGraphic.graphics[graphicIndex].geometry,
                                symbol: highlightSymbol}));
                    } else {
                        if (source.layerView.layer.declaredClass === "esri.layers.MapImageLayer"
                            && featureGraphic.graphics[graphicIndex].geometry.type==="polygon"){
                            this.highlightLayer.add(
                                new EsriGraphic({geometry: featureGraphic.graphics[graphicIndex].geometry,
                                    symbol: highlightSymbol}));
                        } else {
                            this.handles.add(source.layerView.highlight(featureGraphic.graphics[graphicIndex]));
                        }
                    }
                    store?.mapStore.selectFeature(featureGraphic.graphics[graphicIndex]);
                }
            }
        }
    }

    private prepareFeatures(store: IAppStore, graphics: __esri.Graphic[], order?: number,
                            mapPoint?: __esri.Point): void {
        if (!store) {
            return;
        }
        const popupStore = store.PopupStore;
        if (!popupStore) {
            return;
        }

        if (graphics.length > 0) {
            // Reseultate anhand von Sublayers gruppieren. Wird für MapImageLayer benötigt
            const graphicsGroup = new Map();
            const groupMap = new Map();
            graphics.forEach((graphic: __esri.Graphic) => {
                // sourceLayer on Graphic is not documented!
                const source = (graphic as any).sourceLayer as __esri.Sublayer;
                if (source) {
                    const groupSource: __esri.Sublayer | __esri.MapImageLayer | __esri.TileLayer | __esri.ImageryLayer
                        = (!["feature", "imagery"].includes((source as any).type) && source?.parent
                        && (source?.parent as any).type!=="map-image")?
                            source.parent: source;
                    let groupId="";
                    if(groupMap.has(groupSource.id)){
                        groupId=groupMap.get(groupSource.id);
                    }else{
                        groupId=groupSource.id.toString();
                    }
                    if (!graphicsGroup.has(groupId)){
                        const url = PathUtils.getLayerPath(groupSource);
                        let metauid = "";
                        if (typeof url === "string") {
                            metauid = PathUtils.getMetaId(url);
                        }
                        const layerGroup = this.getLayerGroup(metauid, groupSource.id);
                        if(layerGroup){
                            groupId=metauid+layerGroup.layerIds?.join(":");
                            groupMap.set(groupSource.id, groupId);
                        }
                        if (!graphicsGroup.has(groupId)){
                            graphicsGroup.set(groupId,
                                {
                                    title: (layerGroup?.title)? layerGroup.title: groupSource.title,
                                    exclusiveFeature: layerGroup?.exclusiveFeature as boolean,
                                    mergeBy: layerGroup?.mergeBy,
                                    graphics: []
                                }
                            );
                        }
                    }
                    if (!(this.config.exclusiveFeature || graphicsGroup.get(groupId).exclusiveFeature)
                        || graphicsGroup.get(groupId).graphics.length < 1) {
                        let mergeG: any;
                        if(graphicsGroup.get(groupId).mergeBy){
                            const mergeA = graphicsGroup.get(groupId).mergeBy;
                            const mergeV = graphic.getAttribute(mergeA);
                            mergeG =
                                graphicsGroup.get(groupId).graphics.find((g: __esri.Graphic)=>
                                    g.getAttribute(mergeA) === mergeV
                                );
                            if(mergeG) {
                                const newKeys = _.difference(
                                    Object.keys(graphic.attributes), Object.keys(mergeG.attributes));
                                for (const key in mergeG.attributes) {
                                    if (mergeG.attributes.hasOwnProperty(key) && key!=="OBJECTID") {
                                        const newA = graphic.getAttribute(key);
                                        // funktioniert nicht mit Domänen
                                        if (typeof newA !== "undefined" && newA !== mergeG[key]) {
                                            mergeG.setAttribute(graphic.getAttribute(key)+"; " + newA);
                                        }
                                    }
                                }
                                if(newKeys.length>0){
                                    newKeys.forEach((key)=>{
                                        // Alle zusätzlichen Felder ergänzen
                                        mergeG.setAttribute(key, graphic.getAttribute(key));
                                        // Nach Felddefinition im Popup Template suchen
                                        let templateField;
                                        for (const c of (graphic as any).sourceLayer.popupTemplate.content){
                                            if (c.type==="fields"){
                                                templateField = c.fieldInfos.find((f: any)=>f.fieldName===key);
                                                if(typeof templateField !== "undefined"){
                                                    break;
                                                }
                                            }
                                        }
                                        // Gefundenes Feld ergänzen, falls dieses noch nicht vorhanden ist
                                        if(templateField && !mergeG.sourceLayer.popupTemplate.content.some((c: any)=>
                                            c.fieldInfos.find((f: any)=> f.fieldName === key))){
                                            mergeG.sourceLayer.popupTemplate.content[
                                                mergeG.sourceLayer.popupTemplate.content.length-1
                                            ].fieldInfos.push(templateField);

                                        }
                                    });
                                }
                            }
                        }
                        if(!mergeG) {
                            graphicsGroup.get(groupId).graphics.push(graphic);
                        }
                    }
                }
            });
            if (this.config.visibleElements.featureNavigation) {
                if (this.config.exclusiveFeature) {
                    // show only first element
                    graphics = [graphics[0]];
                }
                if (this.firstFlag && mapPoint) {
                    store?.mapStore.view?.openPopup({location: mapPoint, features: graphics});
                    this.firstFlag = false;
                } else {
                    this.popup.features = this.popup.features.concat(graphics);
                }
            } else {
                if (this.firstFlag) {
                    this.firstFlag = false;
                    store?.PopupStore.setVisible(true);
                    this.clearHighlights();
                    this.popup.selectedFeatureIndex = popupStore.panelIndex;
                }
                graphicsGroup.forEach((group) => {
                    popupStore.addPanel({
                        title: group.title,
                        graphics: group.graphics,
                        order
                    });
                    let newIndexes = popupStore.activeIndex;
                    if (this.config.exclusiveFeature) {
                        // only open the exclusive tab
                        newIndexes = [1];
                    } else {
                        if (!popupStore.activeIndex.includes(1)) {
                            newIndexes = [...popupStore.activeIndex, 1].sort();
                        }
                    }
                    // TODO: ev. zusammen mit einer action ausführen
                    popupStore.setActiveIndex(newIndexes);
                    popupStore.setSelectedPanelIndex(1);
                    this.highlight(1, 1, store);
                });
            }
        }
    }

    private addPositionHandles(view: __esri.MapView | __esri.SceneView): void {
        this.watchHandlesPosition.push(
            view.on("resize", () => {
                this.calcPointerPosition();
            })
        );
        this.watchHandlesPosition.push(
            view.watch("center", () => {
                this.calcPointerPosition();
            })
        );
        this.watchHandlesPosition.push(
            view.watch("zoom", () => {
                this.calcPointerPosition();
            })
        );
    }
    private removePositionHandles(view?: __esri.MapView | __esri.SceneView): void {
        if (view) {
            this.clearGraphicLayer(view);
        }
        this.watchHandlesPosition.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandlesPosition.length = 0;
    }
    private calcPointerPosition(): void {
        const { store } = this.props;
        if (!store?.mapStore.view) {
            return;
        }
        const pointer = store?.mapStore.view.graphics.getItemAt(0);
        if (pointer && pointer.geometry.type === "point") {
            const screenPoint = store?.mapStore.view.toScreen(pointer.geometry as __esri.geometry.Point);
            const geoContainer = document.getElementsByClassName("geo-ui-inner-container")[0];
            const visible = !(screenPoint?.x < 0 || screenPoint?.y < 0
                || screenPoint?.x > geoContainer.clientWidth || screenPoint?.y > geoContainer.clientHeight);
            this.setState({pointerPosition: {top: screenPoint?.y, left: screenPoint?.x - 20, width: 40}, visible});
        }
    }

    private prepareLayers(map: __esri.Map, popupStore: PopupStore, store: IAppStore): void {
        map.allLayers.forEach((layer: __esri.Layer) => {
            if (this.preparedLayers.indexOf(layer.id) === -1) {
                this.preparedLayers.push(layer.id);
                layer.load().then((value: any) => {
                    const url = PathUtils.getLayerPath(value);
                    let metauid = "";
                    if (typeof url === "string") {
                        metauid = PathUtils.getMetaId(url);
                    }
                    this.prepareTemplate(value, metauid, popupStore, store);
                    if (value.type === "map-image") {
                        value.allSublayers.forEach((subL: __esri.Sublayer) => {
                            this.prepareTemplate(subL, metauid, popupStore, store);
                        });
                    }
                });
            }
        });
    }

    private getLayerGroup(metauid: string, layerId?: number | string): PopupLayerGroupConfiguration | undefined {
        const layerGroups = this.config.layerGroups;
        if(layerGroups && metauid) {
            // `${metauid}-${layer.id}`
            const layerGroup = layerGroups.find((t)=>
                t.metauid === metauid && (!t.layerIds || t.layerIds.length===0 ||
                    (typeof layerId === "number" && t.layerIds.includes(layerId))));
            return layerGroup;
        }
        return;
    }

    private prepareTemplate(layer: any, metauid: string, popupStore: PopupStore, store: IAppStore): void {
        // In Feldnamen nach Masseinheiten suchen, diese in einer Map ablegen und im Namen entfernen.
        this.watchHandles.push(reactiveUtils.when(
            ()=> layer.popupTemplate?.fieldInfos,
            () => {
                this.prepareUnits(layer, popupStore);
            },
            { initial: true, once: true }
        ));
        this.watchHandles.push(reactiveUtils.when(
            ()=> layer.popupTemplate?.content,
            () => {
                this.prepareUnits(layer, popupStore);
            },
            { initial: true, once: true }
        ));
        const layerTemplate = store.mapViewerConfig.getLayerTemplate(metauid, layer.id);
        // Template aus Konfiguration ergänzen
        if (layerTemplate && typeof layerTemplate.template !== "string") {
            // create a basic template if none exists
            if (typeof layer.popupTemplate === "undefined" || !layer.popupTemplate) {
                layer.popupTemplate = new EsriPopupTemplate();
                layer.popupTemplate.content = [];
                // ensure that a template has a title
                layer.popupTemplate.title = layer.title;
            }
            if (layerTemplate.mode === "append") {
                layer.popupTemplate.content =
                    layer.popupTemplate.content.concat(layerTemplate.template.content);
            } else if (layerTemplate.mode === "prepend") {
                if (Array.isArray(layerTemplate.template.content)) {
                    layer.popupTemplate.content =
                        (layerTemplate.template.content as ContentConfiguration[]).concat(
                            layer.popupTemplate.content);
                }
            } else if (layerTemplate.mode === "replace") {
                layer.popupTemplate = layerTemplate.template;
            }
            if (layerTemplate.template.actions) {
                if (Array.isArray(layer.popupTemplate.actions)){
                    layer.popupTemplate.actions =
                        layer.popupTemplate.actions.concat(layerTemplate.template.actions);
                } else {
                    layer.popupTemplate.actions = layerTemplate.template.actions;
                }
            }
            this.prepareExternalLoader(store, layer);
            this.prepareActions(store, layer);
        }
    }
    private prepareUnits(layer: any, popupStore: PopupStore): void {
        const unitMap = new Map();
        if(layer.popupTemplate?.fieldInfos
            && Array.isArray(layer.popupTemplate.fieldInfos)) {
            layer.popupTemplate.fieldInfos.forEach((fieldInfo: __esri.FieldInfo) => {
                const regExp = new RegExp(" \\[(.+)?\\]( |$)");
                if(regExp.test(fieldInfo.label)) {
                    const matches = fieldInfo.label.match(regExp);
                    if (matches && matches.length === 3) {
                        unitMap.set(fieldInfo.fieldName,
                            matches[1].replace("m2","m²").replace("m3","m³"));
                        fieldInfo.label = fieldInfo.label.replace(regExp, matches[2]);
                    }
                }
            });
        }
        if (layer.popupTemplate?.content && Array.isArray(layer.popupTemplate.content)
            // PopupTemplates mit rasterService ignorieren
            && !layer.popupTemplate.content.some((c) => c.creator==="rasterService")) {
            layer.popupTemplate.content.forEach((content: __esri.FieldsContent) => {
                if(content.type==="fields" && Array.isArray(content.fieldInfos)) {
                    content.fieldInfos.forEach((fieldInfo: __esri.FieldInfo) => {
                        const regExp = new RegExp(" \\[(.+)?\\]( |$)");
                        if (regExp.test(fieldInfo.label)) {
                            const matches = fieldInfo.label.match(regExp);
                            if (matches && matches.length === 3) {
                                unitMap.set(fieldInfo.fieldName,
                                    matches[1].replace("m2", "m²").replace("m3","m³"));
                                fieldInfo.label = fieldInfo.label.replace(regExp, matches[2]);
                            }
                        }
                    });
                }
            });
        }
        if (unitMap.size > 0){
            // Das Property uid ist nicht dokumentiert!
            popupStore.valueUnitMap.set((layer as any).uid, unitMap);
        }
    }

    private prepareExternalLoader(store: IAppStore, layer: any): void {
        if (typeof layer.popupTemplate !== "undefined" && layer.popupTemplate !== null){
            if (Array.isArray(layer.popupTemplate.content)) {
                layer.popupTemplate.content.forEach((content: any) => {
                    if (content.type === "custom" && typeof content.creator === "string") {
                        if (content.creator === "externalServiceLoader" && content.serviceID) {
                            content.creator =
                                (element: any) => this.externalServiceLoader(element.graphic, content.serviceID, layer);
                        }else if (content.creator === "customChart") {
                            content.creator =
                                (element: any) => this.customChart(element.graphic, content);
                        }else if (content.creator === "rasterService") {
                            const targetMetauid = content.serviceID;
                            const template = layer.popupTemplate.clone();
                            template.content = template.content.filter((c) => c.creator !== "rasterService");
                            delete content.creator;
                            delete content.serviceID;
                            this.watchHandles.push(reactiveUtils.watch(
                                ()=> layer.visible,
                                () => {
                                    if(layer.visible){
                                        this.rasterServiceMap.set(layer.uid, targetMetauid);
                                    }else{
                                        this.rasterServiceMap.delete(layer.uid);
                                    }
                                    if(!this.rasterPrepared.includes(layer.uid)) {
                                        const targetLayer = store.mapStore.getLayerViewsByMetauid(targetMetauid);
                                        if(targetLayer.length===1) {
                                            const l = targetLayer[0];
                                            if(layer.hasOwnProperty("maxScale")) {
                                                (l.layer as __esri.ImageryLayer).maxScale =
                                                    Math.min((l.layer as __esri.ImageryLayer).maxScale, layer.maxScale);
                                            }
                                            if(layer.hasOwnProperty("maxScale")){
                                                (l.layer as __esri.ImageryLayer).minScale =
                                                    Math.max((l.layer as __esri.ImageryLayer).minScale, layer.minScale);
                                            }
                                            if(!this.rasterMetauidPrepared.includes(targetMetauid)) {
                                                l.layer.opacity = 0;
                                                (l.layer as __esri.ImageryLayer).legendEnabled = false;
                                                (l.layer as __esri.ImageryLayer).popupTemplate = template;
                                                l.layer.title = template.title;
                                                (l as any).index = store.mapStore.view?.allLayerViews.findIndex(
                                                    (lv) =>
                                                        PathUtils.getMetaId(PathUtils.getLayerPath(lv.layer))
                                                        === targetMetauid);

                                                this.prepareUnits(l.layer, store.PopupStore);
                                            }
                                            this.rasterPrepared.push(layer.uid);
                                            this.rasterMetauidPrepared.push(targetMetauid)
                                        }
                                    }
                                },
                                { initial: true }
                            ));
                        }
                    }
                });
            }
        }
    }

    private prepareActions(store: IAppStore, layer: any): void {
        if (typeof layer.popupTemplate !== "undefined" && layer.popupTemplate !== null){
            if (typeof layer.popupTemplate.actions !== "undefined" && layer.popupTemplate.actions !== null) {
                this.popup.viewModel.includeDefaultActions = false;
                layer.popupTemplate.actions.forEach((actionConfig: ActionButtonConfiguration) => {
                    if (typeof actionConfig.id !== "undefined") {
                        const predefinedAction =
                            store?.appConfig.predefinedActions.find((action) =>
                                actionConfig.id === action.id);
                        // add missing attributes
                        if (predefinedAction) {
                            if (!("title" in actionConfig) || !actionConfig.title){
                                actionConfig.title = predefinedAction.title;
                            }
                            if (predefinedAction.url && (!("url" in actionConfig) || !actionConfig.url)){
                                actionConfig.url = predefinedAction.url;
                            }
                            if (predefinedAction.query && (!("query" in actionConfig) || !actionConfig.query)){
                                actionConfig.query = predefinedAction.query;
                            }
                            if (predefinedAction.icon && (!("icon" in actionConfig) || !actionConfig.icon)){
                                actionConfig.icon = predefinedAction.icon;
                            }
                        }
                        this.actionMap[actionConfig.id] = this.popup.viewModel.allActions.length;
                        this.popup.viewModel.actions.push(actionConfig as EsriActionButton);
                    }
                });
            }
        }
    }

    private clearHighlights(): void {
        this.highlightLayer?.removeAll();
    }
    private clearGraphicLayer(view: __esri.MapView | __esri.SceneView | undefined): void {
        if(view){
            view.graphics.removeAll();
        }
    }

    /**
     * Add click watch to the view
     * Pass view as param to be sure the view is initialized before
     * @param view
     * @param store
     * @private
     */
    private addMapClickWatch(view: __esri.MapView | __esri.SceneView, store: IAppStore): void {
        if (this.mapClickWatchHandle) {
            // watch is already active
            return;
        }
        this.mapClickWatchHandle = view.on("click", (event: __esri.ViewClickEvent) => {
            // Make sure that there is a valid mapPoint
            if (event && event.mapPoint && !store.PopupStore.disabled) {
                runInAction(()=>{
                    store.PopupStore.setFeatures([]);
                    store.PopupStore.setMapPoint(event.mapPoint);
                    store.PopupStore.setVisible(true);
                });
            }
        });
    }

    private removeMapClickWatch(): void {
        this.mapClickWatchHandle?.remove();
        this.mapClickWatchHandle = null;
    }
}

export const Popup = withStore(PopupBase);
